<script setup>
/* Die Startansicht: fester Kopf mit Logo-Umlaufbahn (OrbitHead.vue), darunter die Listen der Seiten, dazu
   eine schmale Leiste (PageBar.vue), die erscheint, sobald der Kopf aus dem Bild gescrollt ist.
   Die Wortmarke darüber (AppHeader.vue) bleibt fest.

   Eine einzige gleitende Seitenposition `pos` (Kommazahl, 0 = erste Seite) treibt beides: die Bahn im Kopf
   dreht sich, die Listen schieben sich. Mathematik, Schwellen und Endlos-Regeln: domain/squadPages.js.

   Wischen gilt NUR im Kopf (Bühne, Titel, Punkte), nicht auf den Listen: stufenlos, beim Loslassen rastet
   die Bahn auf die nächste ganze Seite ein, ein schneller Wisch geht eine Stufe weiter. Klick, Punkte, Pfeile
   animieren (450 ms, ease-out, requestAnimationFrame). Senkrechtes Scrollen bleibt frei: touch-action:
   pan-y, waagerecht wird erst nach kurzer Bewegung entschieden. Nur Touch/Stift; mit der Maus wird nicht
   "gezogen" (Pfeile, Punkte, Logos, Pfeiltasten).

   Wechsel aus der schmalen Leiste (Pfeile und Seiten-Einträge): erst weich nach oben zum Kopf scrollen, dort
   dreht die Bahn. Ein Wechsel unten in der Liste ließe die Seitenhöhe springen und den Scrollstand mitwandern.

   Höhe: Nur die aktive Seite liegt im Fluss und bestimmt die Höhe. Die anderen liegen darüber (absolut,
   oben links) und beeinflussen die Höhe nie — sie sind während der Bewegung sichtbar (schieben sich seitlich
   herein), sonst unsichtbar und inert. Darum ändert sich die Seitenhöhe beim Wischen nicht sprunghaft.

   Scrollposition je Seite wird nicht gemerkt: Nach einem Wechsel steht die Seite oben.
   prefers-reduced-motion: der Wechsel springt ohne Animation, das Hochscrollen darf springen.

   Neue Seite = Eintrag in domain/squadPages.js + Eintrag in PAGE_VIEWS hier + Texte. */
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { mdiArrowUp } from '@mdi/js';
import {
  SQUAD_PAGES, ANIMATION_MS, LIST_VISIBLE_BELOW,
  swipeIntent, dragPosition, settleTarget, easeOutCubic,
  wrapOffset, normalizePos, activeIndexOf, stepTarget, deltaBetween,
  pageIndexFromHash, hashForPage, isPageHash,
} from '../domain/squadPages.js';
import OrbitHead from './OrbitHead.vue';
import PageBar from './PageBar.vue';
import SquadList from './SquadList.vue';
import TransferredList from './TransferredList.vue';
import SelectionBar from './SelectionBar.vue';
import { useSelection } from '../composables/useSelection.js';

defineEmits(['add']);

/* pages/views nur für Tests (z. B. drei Seiten für den Endlos-Lauf); im Betrieb gelten die Vorgaben */
const props = defineProps({
  pages: { type: Array, default: null },
  views: { type: Object, default: null },
});

const PAGE_VIEWS = props.views || { squad: SquadList, transferred: TransferredList };
const pages = props.pages || SQUAD_PAGES.filter((p) => PAGE_VIEWS[p.id]);
const n = pages.length;

/* Gemerkte Seite aus dem URL-Hash: steht sofort da, ohne Drehung der Bahn (kein animateTo beim Start) */
const startIndex = pageIndexFromHash(typeof window !== 'undefined' ? window.location?.hash : '', pages);

/* Markier-Modus: endet beim Wechsel der Seite (Kader <-> Transferierte), die Markierung wird gelöscht */
const { selecting, done: endSelection } = useSelection();

const pos = ref(startIndex);
/* Seite, auf der die Bahn einrastet bzw. gerade liegt (Punkte, Pfeile, inert, Titel für Vorleser) */
const activeIndex = ref(startIndex);
watch(activeIndex, endSelection);
const dragging = ref(false);
/* true, solange gewischt wird oder die Animation läuft: nur dann sind die inaktiven Seiten sichtbar. Es
   beeinflusst NICHT die Höhe (siehe Kommentar oben). */
const moving = ref(false);
const headWrap = ref(null);
const head = ref(null);

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

/* ---------- Animation ---------- */
let frame = 0;
/* Ziel der laufenden Animation (ungewrappt) — mehrere schnelle Tipps zählen ab diesem Ziel, nicht ab der
   halben Drehung dazwischen */
let animTarget = null;
function stopAnimation() {
  if (frame) cancelAnimationFrame(frame);
  frame = 0;
  animTarget = null;
}
const baseTarget = () => (animTarget === null ? Math.round(pos.value) : animTarget);

function animateTo(target) {
  stopAnimation();
  if (reducedMotion() || target === pos.value) {
    pos.value = normalizePos(target, n);
    moving.value = dragging.value;
    return;
  }
  moving.value = true;
  animTarget = target;
  const from = pos.value;
  const t0 = performance.now();
  const tick = (now) => {
    const t = Math.min(1, (now - t0) / ANIMATION_MS);
    pos.value = from + (target - from) * easeOutCubic(t);
    if (t < 1) {
      frame = requestAnimationFrame(tick);
    } else {
      frame = 0;
      animTarget = null;
      pos.value = normalizePos(target, n); // Endlos: zurück in [0, n)
      moving.value = dragging.value;
    }
  };
  frame = requestAnimationFrame(tick);
}

/* Aktive Seite im Hash festhalten. replaceState: kein neuer Verlaufseintrag. Zur ersten Seite zurück wird der
   Hash nur entfernt, wenn er eine Seiten-ID ist — ein fremder Hash (Einladungs-Link) bleibt unberührt. */
function rememberPage(index) {
  if (typeof window === 'undefined' || !window.history?.replaceState || !window.location) return;
  const hash = hashForPage(index, pages);
  if (!hash && !isPageHash(window.location.hash, pages)) return;
  if (hash === window.location.hash) return;
  try {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + hash);
  } catch { /* nicht kritisch: ohne Hash geht die Seite beim Aktualisieren auf die erste zurück */ }
}

/* Auf eine ganze Seite gehen (bei Endlos ungewrappt, z. B. -1 oder n). Die neue Seite startet oben. */
function goTo(target) {
  const next = activeIndexOf(target, n);
  const changed = next !== activeIndex.value;
  activeIndex.value = next;
  rememberPage(next);
  if (changed && window.scrollY > 0) {
    window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
  animateTo(target);
}

const onStep = (delta) => goTo(stepTarget(baseTarget(), delta, n));
/* Punkte: delta ist schon der kürzeste Weg (OrbitHead) */
const onSelect = (delta) => goTo(stepTarget(baseTarget(), delta, n));

/* ---------- Wechsel aus der schmalen Leiste: erst nach oben, dann drehen ---------- */
const SCROLL_WAIT_MS = 700;
let pendingDelta = 0;
let pendingTimer = 0;

function flushPending() {
  clearTimeout(pendingTimer);
  const delta = pendingDelta;
  pendingDelta = 0;
  if (!delta) return;
  let target = baseTarget();
  for (let i = 0; i < Math.abs(delta); i += 1) target = stepTarget(target, Math.sign(delta), n);
  goTo(target);
}

/* Eintrag der Leiste: Schrittzahl zur Zielseite (bei Endlos der kürzeste Weg), dann wie ein Pfeil */
function onBarGoto(index) {
  const delta = deltaBetween(activeIndex.value, index, n);
  if (delta) onBarStep(delta);
}

function onBarStep(delta) {
  if (window.scrollY <= 1) {
    onStep(delta);
    return;
  }
  pendingDelta += delta;
  if (reducedMotion()) {
    window.scrollTo({ top: 0, behavior: 'auto' });
    flushPending();
    return;
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
  clearTimeout(pendingTimer);
  pendingTimer = setTimeout(flushPending, SCROLL_WAIT_MS); // Rückfall, falls das Scroll-Ende nicht gemeldet wird
}

/* ---------- Wischen (nur im Kopf) ---------- */
let gesture = null;
let swallowClick = false;

function onPointerDown(e) {
  if (e.pointerType === 'mouse' || !e.isPrimary || n < 2) return;
  gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), intent: null, startPos: pos.value };
}

function onPointerMove(e) {
  if (!gesture || e.pointerId !== gesture.id) return;
  const dx = e.clientX - gesture.x;
  const dy = e.clientY - gesture.y;
  if (!gesture.intent) {
    gesture.intent = swipeIntent(dx, dy);
    if (gesture.intent === 'horizontal') {
      stopAnimation();
      gesture.startPos = pos.value;
      dragging.value = true;
      moving.value = true;
      /* Fängt die Geste ein: Das Loslassen löst keinen Klick auf Knopf oder Logo darunter aus. */
      try { headWrap.value?.setPointerCapture(e.pointerId); } catch { /* nicht kritisch */ }
    }
  }
  if (gesture.intent === 'horizontal') {
    pos.value = dragPosition({ startPos: gesture.startPos, dx, windowWidth: window.innerWidth, n });
    activeIndex.value = activeIndexOf(pos.value, n);
  }
}

function endGesture(e, cancelled) {
  if (!gesture || e.pointerId !== gesture.id) return;
  const g = gesture;
  gesture = null;
  if (g.intent !== 'horizontal') return;
  dragging.value = false;
  swallowClick = true;
  setTimeout(() => { swallowClick = false; }, 0);
  const dx = e.clientX - g.x;
  const target = cancelled
    ? Math.round(g.startPos)
    : settleTarget({ pos: pos.value, startPos: g.startPos, dx, dt: performance.now() - g.t, n });
  goTo(target);
}

function onClickCapture(e) {
  if (!swallowClick) return;
  e.stopPropagation();
  e.preventDefault();
}

/* ---------- Seiten der Liste ----------
   Alle Seiten liegen übereinander (die aktive im Fluss, die anderen absolut) und werden um d Seitenbreiten
   verschoben. Sichtbar nur bei |d| < LIST_VISIBLE_BELOW. */
function slideStyle(i) {
  const d = wrapOffset(i - pos.value, n);
  const hidden = Math.abs(d) >= LIST_VISIBLE_BELOW;
  const shift = d * 100;
  return {
    transform: shift === 0 ? 'none' : `translateX(${shift}%)`,
    visibility: hidden ? 'hidden' : undefined,
  };
}

/* ---------- Scrollen: schmale Leiste, Nach-oben-Knopf ----------
   Der Nach-oben-Knopf liegt bewusst außerhalb der verschobenen Seiten: Ein transformiertes Elternelement
   würde position: fixed an sich binden. Entscheidung Bastian, 2026-09-23. */
const showScrollTop = ref(false);
const showBar = ref(false);
const SCROLL_THRESHOLD = 400;

/* Höhe der festen oberen Leiste (TopBar.vue veröffentlicht sie als --topbar-h) */
function topbarHeight() {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h'));
  return Number.isFinite(v) ? v : 0;
}

/* Die Leiste erscheint erst, wenn der GANZE Kopf (Bühne, Titel, Segmente) unter der oberen Leiste verschwunden
   ist. Gemessen wird der Wisch-Bereich (pager-head), nicht nur die Bühne: Bis 2026-10 zählte die Unterkante der
   Bühne, die Leiste erschien dadurch, während Titel und Segmente noch darunter sichtbar waren — und unter der
   Leiste blieb ein leerer Block bis zur Suche. */
function updateBar() {
  const el = headWrap.value;
  showBar.value = !!el && el.getBoundingClientRect().bottom <= topbarHeight();
}

function onWindowScroll() {
  showScrollTop.value = window.scrollY > SCROLL_THRESHOLD;
  updateBar();
  if (pendingDelta !== 0 && window.scrollY <= 1) flushPending();
}
function scrollToTop() {
  window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
}
onMounted(() => {
  window.addEventListener('scroll', onWindowScroll, { passive: true });
  window.addEventListener('resize', updateBar);
  updateBar();
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onWindowScroll);
  window.removeEventListener('resize', updateBar);
  stopAnimation();
  clearTimeout(pendingTimer);
  /* Ausloggen (Pager verschwindet): keine Seite für den nächsten Nutzer mitnehmen */
  rememberPage(0);
});
</script>

<template>
  <div class="pager" :class="{ moving, 'sel-on': selecting }">
    <PageBar :pages="pages" :active-index="activeIndex" :visible="showBar" @step="onBarStep" @goto="onBarGoto" />

    <!-- Nur hier wird gewischt -->
    <div
      ref="headWrap"
      class="pager-head"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="endGesture($event, false)"
      @pointercancel="endGesture($event, true)"
      @click.capture="onClickCapture"
    >
      <OrbitHead ref="head" :pages="pages" :pos="pos" :active-index="activeIndex" @step="onStep" @select="onSelect" />
    </div>

    <SelectionBar />

    <div class="pager-viewport">
      <section
        v-for="(p, i) in pages"
        :id="`squad-page-${p.id}`"
        :key="p.id"
        class="pager-slide"
        :class="{ active: i === activeIndex }"
        :style="slideStyle(i)"
        role="tabpanel"
        :aria-labelledby="`squad-tab-${p.id}`"
        :inert="i === activeIndex ? undefined : ''"
        :data-page="p.id"
      >
        <component :is="PAGE_VIEWS[p.id]" @add="$emit('add')" />
      </section>
    </div>

    <button
      v-if="showScrollTop"
      type="button"
      class="scroll-top-btn"
      :title="$t('squad.scrollToTop')"
      :aria-label="$t('squad.scrollToTop')"
      @click="scrollToTop"
    >
      <v-icon :icon="mdiArrowUp" size="20" />
    </button>
  </div>
</template>

<style scoped>
/* Handy: die feste Leiste unten darf den letzten Spieler nicht verdecken */
@media (max-width: 1023px) {
  .pager.sel-on {
    padding-bottom: calc(72px + env(safe-area-inset-bottom));
  }
}
.pager-head {
  /* Senkrecht scrollt der Browser, waagerecht gehört die Geste uns — nur hier, nicht auf den Listen */
  touch-action: pan-y;
}
.pager-viewport {
  position: relative;
  /* Nur seitlich beschneiden: Eine höhere Nachbarseite darf nach unten überstehen, ohne die Höhe zu ändern.
     Erst hidden als Rückfall (Browser ohne clip, vor Safari 16: kein waagerechtes Scrollen der Seite),
     dann clip, wo es geht. */
  overflow-x: hidden;
  overflow-x: clip;
}
.pager-slide {
  width: 100%;
  min-width: 0;
}
/* Nur die aktive Seite liegt im Fluss und bestimmt die Höhe; die anderen liegen darüber */
.pager-slide:not(.active) {
  position: absolute;
  top: 0;
  left: 0;
}
/* Ruhend sind die inaktiven Seiten unsichtbar; sichtbar sind sie nur, solange die Bahn sich bewegt.
   display:none statt nur visibility:hidden: eine unsichtbare, aber hohe Nachbarseite (absolut, nach unten überstehend)
   würde sonst die Scrollhöhe der ganzen Seite verlängern — unter einer kurzen Liste (Transferred) endet die Seite dann
   in einer leeren, dunklen Fläche. Die Komponenten bleiben eingehängt, nur das Layout entfällt. */
.pager:not(.moving) .pager-slide:not(.active) {
  display: none;
}
.scroll-top-btn {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 5;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--panel);
  border: 1px solid var(--border);
  color: var(--muted);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: color 0.14s, border-color 0.14s;
}
.scroll-top-btn:hover {
  color: var(--green);
  border-color: var(--green-dim);
}
</style>
